import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader, Bot, User, Sparkles, AlertCircle, Lightbulb } from 'lucide-react';
import { getResearchAssistantResponse } from '../lib/xai';
import { getProfile } from '../lib/api';
import type { Profile } from '../lib/types';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  suggestions?: string[];
}

export function ResearchAssistant() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await getProfile();
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    setError(null);
    const userMessage: Message = {
      role: 'user',
      content: input.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await getResearchAssistantResponse(input.trim(), {
        studentProfile: profile
      });

      if (response.error) {
        throw new Error(response.error);
      }

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: response.message,
          suggestions: response.suggestions
        }
      ]);
    } catch (error) {
      console.error('Assistant error:', error);
      setError(
        error instanceof Error 
          ? error.message 
          : 'Unable to process your request. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 shadow-sm rounded-lg h-[500px] flex flex-col">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Sparkles className="h-5 w-5 text-indigo-400" />
          <h3 className="text-lg font-medium text-white">Research Assistant</h3>
        </div>
        <p className="text-sm text-gray-400">
          Ask me anything about your research journey
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-900/20 border-b border-red-800/30">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-start space-x-2 ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0">
                <Sparkles className="h-8 w-8 rounded-full bg-indigo-900/50 p-1.5 text-indigo-400" />
              </div>
            )}
            <div className="flex flex-col space-y-2 max-w-[80%]">
              <div
                className={`rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-700 text-gray-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
              {message.suggestions && message.suggestions.length > 0 && (
                <div className="ml-2 space-y-1">
                  {message.suggestions.map((suggestion, i) => (
                    <div key={i} className="flex items-center text-sm text-gray-400">
                      <Lightbulb className="h-4 w-4 mr-1.5 text-yellow-500" />
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {message.role === 'user' && (
              <div className="flex-shrink-0">
                <User className="h-8 w-8 rounded-full bg-indigo-600 p-1.5 text-white" />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-700">
        <div className="flex space-x-4">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about research, professors, or opportunities..."
            className="flex-1 rounded-md border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 bg-gray-900 text-white px-4 py-2" // Added px-4 for more padding
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}